import { TooltipComponent } from '@components/core/tooltip.component';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import type { SelectOption } from '@utils/select.util';
import { XIcon } from 'lucide-react';
import type { FC, ReactNode } from 'react';

type CapabilityDefinitionTooltipProps = {
  definition?: ReactNode;
  side?: 'bottom' | 'left' | 'right' | 'top';
  trigger?: ReactNode;
};

type CapabilitySelectOptionProps = {
  option: Pick<SelectOption, 'description' | 'label'>;
};

type CapabilitySelectedBadgeProps = {
  description?: string;
  label: string;
  remove: () => void;
};

export const CapabilityDefinitionTooltip: FC<CapabilityDefinitionTooltipProps> = (props) => {
  const { definition, side = 'right', trigger } = props;

  if (!definition) {
    return trigger ? <>{trigger}</> : null;
  }

  return (
    <TooltipComponent
      className="text-xs whitespace-pre-wrap font-normal"
      side={side}
      trigger={trigger}
    >
      {definition}
    </TooltipComponent>
  );
};

export const CapabilitySelectOption: FC<CapabilitySelectOptionProps> = (props) => {
  const { option } = props;

  return (
    <div className="flex min-w-0 flex-col gap-0.5 py-1">
      <span className="text-sm font-medium leading-tight text-gray-900">{option.label}</span>
      {option.description && (
        <span className="whitespace-normal text-xs leading-snug text-muted-foreground">{option.description}</span>
      )}
    </div>
  );
};

export const CapabilitySelectedBadge: FC<CapabilitySelectedBadgeProps> = (props) => {
  const { description, label, remove } = props;

  const badge = (
    <Badge className={cn('border-transparent bg-gray-200 text-gray-700 hover:bg-gray-200')}>
      <span>{label}</span>
      <CapabilityDefinitionTooltip definition={description} />
      <XIcon
        className="ml-1 h-4 w-4 cursor-pointer hover:text-red-500"
        onClick={(event) => {
          event.stopPropagation();
          remove();
        }}
      />
    </Badge>
  );

  return badge;
};
